import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, FileText, MapPin, Edit3, Phone, Navigation, Upload, Download, CheckCircle, AlertCircle } from 'lucide-react';
import { OwnerLayout } from '../../components/OwnerLayout';
import api from '../../api';

function getInitials(name) {
  return name?.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'M';
}

function formatDateTime(d) {
  if (!d) return '';
  try {
    const dt = new Date(d);
    return dt.toLocaleString('en-IN', { 
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true 
    });
  } catch { return d; }
}

function openMaps(lat, lng) {
  if (lat && lng) {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  }
}

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`} style={{ bottom: '24px' }}>{msg}</div>;
}

export default function OwnerMerchants() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [merchants, setMerchants] = useState([]);
  const [agents, setAgents] = useState([]);
  const [services, setServices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [agentFilter, setAgentFilter] = useState(searchParams.get('agentId') || '');
  const [serviceFilter, setServiceFilter] = useState(searchParams.get('service') || '');
  const [toast, setToast] = useState({ show: false, msg: '' });
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const loadFilters = useCallback(async () => {
    try {
      const [agentsRes, servicesRes] = await Promise.all([
        api.get('/agents'),
        api.get('/services/all')
      ]);
      setAgents(agentsRes.data || []);
      setServices(servicesRes.data || []);
    } catch (err) { console.error(err); }
  }, []);

  const loadMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (agentFilter) params.append('agentId', agentFilter);
      if (serviceFilter) params.append('service', serviceFilter);
      const createdFrom = searchParams.get('createdFrom');
      const createdTo = searchParams.get('createdTo');
      if (createdFrom) params.append('createdFrom', createdFrom);
      if (createdTo) params.append('createdTo', createdTo);
      const res = await api.get(`/merchants?${params}`);
      setMerchants(res.data.merchants || []);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, statusFilter, agentFilter, serviceFilter, page, searchParams]);

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { loadMerchants(); }, [loadMerchants]);

  const handleStatusChange = async (merchantId, newStatus) => {
    try {
      await api.patch(`/merchants/${merchantId}/status`, { status: newStatus });
      setMerchants(prev => prev.map(m => m.id === merchantId ? { ...m, status: newStatus } : m));
      showToast(`Status updated to ${newStatus}`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleAddOwnerNote = async () => {
    if (!selectedMerchant || !noteText.trim()) return;
    try {
      await api.post(`/merchants/${selectedMerchant.id}/owner-note`, { note: noteText });
      showToast('Note added');
      setNoteText('');
      setSelectedMerchant(null);
    } catch { showToast('Failed to add note'); }
  };

  const handleDelete = async (merchantId) => {
    if (!window.confirm('Delete this merchant? This cannot be undone.')) return;
    try {
      await api.delete(`/merchants/${merchantId}`);
      setMerchants(prev => prev.filter(m => m.id !== merchantId));
      showToast('Merchant deleted');
    } catch { showToast('Failed to delete'); }
  };

  const handleDownloadSample = async () => {
    try {
      const res = await api.get('/merchants/import/sample', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'merchant_import_sample.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { showToast('Failed to download sample'); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      showToast('Please select a CSV file');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/merchants/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      if (res.data.imported > 0) {
        showToast(`${res.data.imported} merchant(s) imported!`);
        await loadMerchants();
      }
    } catch (err) {
      showToast(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <OwnerLayout title="Merchants">
      {/* Filters */}
      <div className="panel mb-4">
        <div className="p-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="search-wrap flex-1" style={{ background: 'var(--g1)' }}>
              <Search size={14} color="var(--g3)" />
              <input
                className="search-input"
                placeholder="Search by name, phone, or business…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                data-testid="merchant-search"
              />
            </div>
            <select
              className="form-select"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto', minWidth: '120px' }}
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              <option value="New">New</option>
              <option value="Contacted">Contacted</option>
              <option value="Onboarded">Onboarded</option>
            </select>
            <select
              className="form-select"
              value={agentFilter}
              onChange={e => { setAgentFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto', minWidth: '140px' }}
              data-testid="agent-filter"
            >
              <option value="">All Agents</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select
              className="form-select"
              value={serviceFilter}
              onChange={e => { setServiceFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto', minWidth: '140px' }}
              data-testid="service-filter"
            >
              <option value="">All Services</option>
              {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Results Summary + Import Button */}
      <div className="flex items-center justify-between mb-3" style={{ flexWrap: 'wrap', gap: '8px' }}>
        <div className="text-sm text-gray">
          Showing {merchants.length} of {total} merchants
        </div>
        <button
          className="od-import-btn"
          onClick={() => setShowImport(!showImport)}
          data-testid="toggle-import-btn"
        >
          <Upload size={14} /> Import Merchants
        </button>
      </div>

      {/* Import Panel */}
      {showImport && (
        <div className="panel mb-4 od-import-panel" data-testid="import-panel">
          <div className="od-import-head">
            <div className="od-import-title">Import Merchants from CSV</div>
            <div className="od-import-desc">Upload a CSV file to bulk-import merchants. Download the sample file to see the required format.</div>
          </div>
          <div className="od-import-body">
            <div className="od-import-steps">
              <div className="od-import-step">
                <span className="od-step-num">1</span>
                <div>
                  <div className="od-step-title">Download Sample CSV</div>
                  <div className="od-step-desc">Get the template with correct column headers</div>
                  <button className="od-step-btn" onClick={handleDownloadSample} data-testid="download-sample-btn">
                    <Download size={13} /> Download Sample
                  </button>
                </div>
              </div>
              <div className="od-import-step">
                <span className="od-step-num">2</span>
                <div>
                  <div className="od-step-title">Fill in merchant data</div>
                  <div className="od-step-desc">Business Name, Owner Name, Phone (10 digits) are required. Services should match names from the system.</div>
                </div>
              </div>
              <div className="od-import-step">
                <span className="od-step-num">3</span>
                <div>
                  <div className="od-step-title">Upload the CSV file</div>
                  <div className="od-step-desc">Duplicate phone numbers will be skipped.</div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleImportFile}
                    style={{ display: 'none' }}
                    data-testid="import-file-input"
                  />
                  <button
                    className="od-step-btn primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    data-testid="upload-csv-btn"
                  >
                    <Upload size={13} /> {importing ? 'Importing...' : 'Choose CSV & Import'}
                  </button>
                </div>
              </div>
            </div>

            {/* Import Results */}
            {importResult && (
              <div className="od-import-result" data-testid="import-result">
                {importResult.imported > 0 && (
                  <div className="od-result-success">
                    <CheckCircle size={14} /> {importResult.imported} merchant(s) imported successfully
                  </div>
                )}
                {importResult.skipped > 0 && (
                  <div className="od-result-warn">
                    <AlertCircle size={14} /> {importResult.skipped} row(s) skipped
                  </div>
                )}
                {importResult.errors?.length > 0 && (
                  <div className="od-result-errors">
                    <div className="od-result-errors-title">Details:</div>
                    {importResult.errors.map((err, i) => (
                      <div key={i} className="od-result-error-row">{err}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Merchants Table */}
      <div className="panel">
        {loading ? (
          <div className="p-8 text-center text-gray">Loading…</div>
        ) : merchants.length === 0 ? (
          <div className="p-8 text-center text-gray">
            <div style={{ fontSize: '28px', marginBottom: '8px' }}>🏪</div>
            <div className="font-heading font-bold text-navy">No merchants found</div>
            <div className="text-sm mt-1">Try adjusting your filters</div>
          </div>
        ) : (
          <>
            {/* Desktop Table - Hidden on Mobile */}
            <div className="overflow-x-auto hidden lg:block">
              <table className="ctable">
                <thead>
                  <tr>
                    <th>Business</th>
                    <th>Owner / Phone</th>
                    <th>Agent</th>
                    <th>Services</th>
                    <th>Status</th>
                    <th>Registered</th>
                    <th>Onboarded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {merchants.map(m => (
                    <tr key={m.id} data-testid={`merchant-row-${m.id}`}>
                      <td>
                        <div className="t-agent">
                          <div className="t-av">{getInitials(m.businessName)}</div>
                          <div>
                            <div className="t-name">{m.businessName}</div>
                            <div className="t-id">{m.city || m.address || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="t-name">{m.ownerName}</div>
                        <a href={`tel:${m.phone}`} className="t-id clickable-phone" data-testid={`call-${m.id}`}>
                          <Phone size={10} /> {m.phone}
                        </a>
                      </td>
                      <td>
                        <div className="text-sm font-medium">{m.agentName || '—'}</div>
                        <div className="text-xs text-gray">{m.agentEmployeeId || ''}</div>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {(m.services || []).slice(0, 2).map(s => (
                            <span key={s.id} className={`svc-pill ${s.slug === 'upi_qr' ? 'upi' : ''}`}>
                              {s.name}
                            </span>
                          ))}
                          {(m.services?.length || 0) > 2 && (
                            <span className="svc-pill">+{m.services.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <select
                          className={`badge-${(m.status || 'new').toLowerCase()}`}
                          value={m.status}
                          onChange={e => handleStatusChange(m.id, e.target.value)}
                          style={{ cursor: 'pointer', border: 'none', background: 'inherit' }}
                          data-testid={`status-${m.id}`}
                        >
                          <option value="New">New</option>
                          <option value="Contacted">Contacted</option>
                          <option value="Onboarded">Onboarded</option>
                        </select>
                      </td>
                      <td>
                        <div className="text-sm">{formatDateTime(m.createdAt)}</div>
                        {m.gps_lat && (
                          <button 
                            className="text-xs text-green-600 flex items-center gap-1 cursor-pointer hover:underline"
                            onClick={() => openMaps(m.gps_lat, m.gps_lng)}
                            data-testid={`gps-${m.id}`}
                          >
                            <Navigation size={10} /> View on Map
                          </button>
                        )}
                      </td>
                      <td>
                        {m.status === 'Onboarded' && m.onboardedAt ? (
                          <div className="text-sm" style={{ color: '#16a34a', fontWeight: 600 }}>{formatDateTime(m.onboardedAt)}</div>
                        ) : (
                          <span className="text-xs text-gray">—</span>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            className="btn-outline"
                            style={{ padding: '4px 8px', fontSize: '11px' }}
                            onClick={() => navigate(`/owner/merchants/${m.id}/edit`)}
                            data-testid={`edit-${m.id}`}
                          >
                            <Edit3 size={12} /> Edit
                          </button>
                          <button
                            className="btn-outline"
                            style={{ padding: '4px 8px', fontSize: '11px' }}
                            onClick={() => setSelectedMerchant(m)}
                            data-testid={`add-note-${m.id}`}
                          >
                            <FileText size={12} /> Note
                          </button>
                          <button
                            className="btn-outline red"
                            style={{ padding: '4px 8px', fontSize: '11px' }}
                            onClick={() => handleDelete(m.id)}
                            data-testid={`delete-${m.id}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards - Hidden on Desktop */}
            <div className="lg:hidden">
              {merchants.map(m => (
                <div key={m.id} className="mcard" data-testid={`merchant-mobile-${m.id}`} style={{ margin: '0', borderRadius: 0, borderTop: 0 }}>
                  <div className="mc-body" style={{ padding: '12px 16px' }}>
                    <div className="mc-top">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="mc-nm">{m.businessName}</div>
                        <div className="mc-ow">{m.ownerName}</div>
                      </div>
                      <select
                        className={`badge-${(m.status || 'new').toLowerCase()}`}
                        value={m.status}
                        onChange={e => handleStatusChange(m.id, e.target.value)}
                        style={{ cursor: 'pointer', border: 'none', background: 'inherit', fontSize: '10px' }}
                      >
                        <option value="New">New</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Onboarded">Onboarded</option>
                      </select>
                    </div>
                    <div className="mc-meta" style={{ marginTop: '6px' }}>
                      <a href={`tel:${m.phone}`} className="mc-mi">
                        <Phone size={12} /> {m.phone}
                      </a>
                      <span className="mc-mi">👤 {m.agentName || 'N/A'}</span>
                    </div>
                    <div className="mc-meta" style={{ marginTop: '4px' }}>
                      <span className="text-xs text-gray">{formatDateTime(m.createdAt)}</span>
                      {m.gps_lat && (
                        <button 
                          className="gps-ok clickable"
                          onClick={() => openMaps(m.gps_lat, m.gps_lng)}
                        >
                          <Navigation size={10} /> Map
                        </button>
                      )}
                    </div>
                    {m.status === 'Onboarded' && m.onboardedAt && (
                      <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '4px', fontWeight: 600 }}>
                        Onboarded: {formatDateTime(m.onboardedAt)}
                      </div>
                    )}
                    {(m.services || []).length > 0 && (
                      <div className="mc-tags" style={{ marginTop: '6px' }}>
                        {m.services.slice(0, 3).map(s => (
                          <span key={s.id} className={`stag ${s.slug === 'upi_qr' ? 'upi' : ''}`}>{s.name}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 mt-2 pt-2" style={{ borderTop: '1px solid var(--g1)' }}>
                      <button
                        className="btn-outline"
                        style={{ padding: '6px 10px', fontSize: '11px', flex: 1 }}
                        onClick={() => navigate(`/owner/merchants/${m.id}/edit`)}
                      >
                        <Edit3 size={12} style={{ marginRight: '4px' }} /> Edit
                      </button>
                      <button
                        className="btn-outline"
                        style={{ padding: '6px 10px', fontSize: '11px', flex: 1 }}
                        onClick={() => setSelectedMerchant(m)}
                      >
                        <FileText size={12} style={{ marginRight: '4px' }} /> Note
                      </button>
                      <button
                        className="btn-outline red"
                        style={{ padding: '6px 10px', fontSize: '11px' }}
                        onClick={() => handleDelete(m.id)}
                      >
                        Del
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-gray-100">
            <button className="btn-outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span className="px-3 py-2 text-sm text-gray">{page} / {pages}</span>
            <button className="btn-outline" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>

      {/* Owner Note Modal */}
      {selectedMerchant && (
        <div className="owner-modal-overlay open" onClick={e => { if (e.target === e.currentTarget) setSelectedMerchant(null); }}>
          <div className="owner-modal" style={{ width: '480px' }}>
            <div className="modal-head">
              <div className="modal-icon"><FileText size={16} /></div>
              <div>
                <div className="modal-title">Add Private Note</div>
                <div className="modal-subtitle">{selectedMerchant.businessName}</div>
              </div>
              <button className="modal-close" onClick={() => setSelectedMerchant(null)}>×</button>
            </div>
            <div className="modal-body">
              {selectedMerchant.ownerNotes?.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray uppercase mb-2">Previous Notes</div>
                  {selectedMerchant.ownerNotes.map((n, i) => (
                    <div key={i} className="prev-note">
                      <div className="dt">{formatDateTime(n.addedAt)}</div>
                      <div className="nt">{n.note}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">New Note (Private to Owner)</label>
                <textarea
                  className="form-input"
                  rows={3}
                  placeholder="Add a private note about this merchant…"
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  data-testid="owner-note-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn-outline" onClick={() => setSelectedMerchant(null)}>Cancel</button>
                <button className="btn-navy" onClick={handleAddOwnerNote} data-testid="save-owner-note">Save Note</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast.msg} show={toast.show} />
    </OwnerLayout>
  );
}
